import type { PointerEvent } from "react";
import { eloSelectionOpacity, eloSelectionHoverHandlers } from "./C1_eloSelectionFocus";
import { C1_Opening } from "../model/C1_Opening";
import c1_state from "../model/C1_State";
import type { ObjectDragPreview, Rect } from "../types/C1_AppTypes";
import { C1_DoorView } from "./C1_DoorView";
import { C1_WindowView } from "./C1_WindowView";
import { planObjectKey } from "./C1_planObjectKey";

type OpeningsViewProps = {
  openings: C1_Opening[];
  selectedOpeningId?: string;
  onStartDrag: (event: PointerEvent, openingId: string) => void;
  onStartOpeningResize: (event: PointerEvent, openingId: string, edge: "start" | "end") => void;
  onObjectDoubleClick?: (bounds: Rect, clientPoint: { x: number; y: number }) => void;
  preview?: Extract<ObjectDragPreview, { type: "opening" }>;
  structureOpacity?: number;
};

/** Builds transient opening facets for rendering without creating ESTADA objects. */
function displayedOpenings(openings: C1_Opening[], preview?: Extract<ObjectDragPreview, { type: "opening" }>) {
  const entries: Array<{ source: C1_Opening; displayed: C1_Opening }> = [];
  for (const opening of openings) {
    if (!preview || preview.openingId !== opening.id) {
      entries.push({ source: opening, displayed: opening });
      continue;
    }

    entries.push({ source: opening, displayed: preview.opening });
  }
  return entries;
}

export function C1_OpeningsSvgView({
  openings,
  selectedOpeningId,
  onStartDrag,
  onStartOpeningResize,
  onObjectDoubleClick,
  preview,
  structureOpacity = 1,
}: OpeningsViewProps) {
  const entries = displayedOpenings(openings, preview);
  const renderingPdf = c1_state.floorImageRenderOptions.value !== null;
  const openingsInteractive = c1_state.editMode.value !== "furnishing";
  const selection = c1_state.selection;
  const drag = c1_state.drag?.value;
  const draggedOpeningId = drag?.type === "opening" || drag?.type === "opening-resize" ? drag.openingId : undefined;
  let selectedEloOwner = selection?.className === "C1_EloItem" ? selection.parent : undefined;
  while (selectedEloOwner?.className === "C1_EloItem") selectedEloOwner = selectedEloOwner.parent;
  const zoomToOpening = (opening: C1_Opening, clientPoint: { x: number; y: number }) => {
    const center = opening.absolutePosition;
    const horizontal = opening.side === "top" || opening.side === "bottom";
    const along = opening.width;
    const across = opening.kind === "window" ? Math.max(0.25, opening.width * 0.15) : opening.width;
    const width = horizontal ? along : across;
    const height = horizontal ? across : along;
    onObjectDoubleClick?.(
      { x1: center.x - width / 2, y1: center.y - height / 2, x2: center.x + width / 2, y2: center.y + height / 2 },
      clientPoint,
    );
  };
  return (
    <g data-c1-svg-layer="openings">
      {entries.map(({ source, displayed }) => (
        <g key={planObjectKey(source)} data-c1-object-id={source.id} opacity={eloSelectionOpacity(source, c1_state) * (
          selectedOpeningId === source.id || selectedOpeningId === displayed.id || selectedEloOwner?.id === source.id ||
          preview?.openingId === source.id || draggedOpeningId === source.id
            ? 1 : structureOpacity
        )} {...eloSelectionHoverHandlers(source, c1_state)}>
        {displayed.kind === "window" ? (
          <C1_WindowView
            key={planObjectKey(source)}
            opening={displayed}
            interactive={openingsInteractive}
            selected={!renderingPdf && !!selectedOpeningId && selectedOpeningId === displayed.id}
            indirectlySelected={!renderingPdf && selectedEloOwner?.id === displayed.id}
            onSelectOpening={() => c1_state.setSelection(source)}
            onStartDrag={onStartDrag}
            onStartResize={onStartOpeningResize}
            onDoubleClick={(_, clientPoint) => zoomToOpening(source, clientPoint)}
          />
        ) : (
          <C1_DoorView
            key={planObjectKey(source)}
            opening={displayed}
            interactive={openingsInteractive}
            selected={!renderingPdf && !!selectedOpeningId && selectedOpeningId === displayed.id}
            indirectlySelected={!renderingPdf && selectedEloOwner?.id === displayed.id}
            onSelectOpening={() => c1_state.setSelection(source)}
            onStartDrag={onStartDrag}
            onStartResize={onStartOpeningResize}
            onDoubleClick={(_, clientPoint) => zoomToOpening(source, clientPoint)}
          />
        )}
        </g>
      ))}
    </g>
  );
}
